/*!
 * Copyright 2005-2023 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, ViewEncapsulation, ViewChild, Input } from '@angular/core';
import { NgIf } from '@angular/common';

import {
  PdfViewerModule,
  PdfViewerComponent as SyncfusionPdfViewerComponent,
  LinkAnnotationService,
  BookmarkViewService,
  MagnificationService,
  ThumbnailViewService,
  ToolbarService,
  NavigationService,
  TextSearchService,
  TextSelectionService,
  PrintService,
  AnnotationService,
  FormDesignerService,
  FormFieldsService,
  PageOrganizerService,
  CustomToolbarItemModel
} from '@syncfusion/ej2-angular-pdfviewer';
import { ReviewerService } from './reviewer.service';
import { createSpinner, showSpinner, hideSpinner } from '@syncfusion/ej2-popups';
import { DialogModule } from '@syncfusion/ej2-angular-popups';
import { NotificationService } from '@alfresco/adf-core';

import { WatermarkModule } from '../../../../viewer/src/lib/components/watermark/watermark.module';
import { ConfigExtensionService } from '../service/config-extension.service';
import { PeopleContentService } from '@alfresco/adf-content-services';
import moment from 'moment';
import { NodeEntry } from '@alfresco/js-api';

export const Toolbar_NoAnnotation = {
  showTooltip: true,
  showText: true,
  toolbarItems: [
    // 'OpenOption',
    'UndoRedoTool',
    'PageNavigationTool',
    'MagnificationTool',
    'PanTool',
    'SelectionTool',
    'SubmitForm',
    'FreeTextAnnotationOption',
    'InkAnnotationOption',
    'ShapeAnnotationOption',
    'StampAnnotation',
    'SignatureOption',
    'SearchOption',
    {
      prefixIcon: 'e-icons e-transform-left',
      id: 'rotateAnticlockwise',
      tooltipText: 'Rotate Anticlockwise'
    } as CustomToolbarItemModel,
    {
      prefixIcon: 'e-icons e-transform-right',
      id: 'rotateClockwise',
      tooltipText: 'Rotate Clockwise'
    } as CustomToolbarItemModel,
    {
      text: 'Rotate All',
      id: 'rotateall',
      tooltipText: 'Rotate All'
    } as CustomToolbarItemModel
  ],
  annotationToolbarItems: [
    'HighlightTool',
    'UnderlineTool',
    'StrikethroughTool',
    'ColorEditTool',
    'OpacityEditTool',
    'AnnotationDeleteTool',
    'StampAnnotationTool',
    'HandWrittenSignatureTool',
    'InkAnnotationTool',
    'ShapeTool',
    'CalibrateTool',
    'StrokeColorEditTool',
    'ThicknessEditTool',
    'FreeTextAnnotationTool',
    'FontFamilyAnnotationTool',
    'FontSizeAnnotationTool',
    'FontStylesAnnotationTool',
    'FontAlignAnnotationTool',
    'FontColorAnnotationTool',
    'CommentPanelTool'
  ]
};

@Component({
  selector: 'app-reviewer',
  standalone: true,
  imports: [PdfViewerModule, DialogModule, WatermarkModule, NgIf],
  providers: [
    LinkAnnotationService,
    BookmarkViewService,
    MagnificationService,
    ThumbnailViewService,
    ToolbarService,
    NavigationService,
    AnnotationService,
    TextSearchService,
    TextSelectionService,
    PrintService,
    FormDesignerService,
    FormFieldsService,
    PageOrganizerService
  ],
  templateUrl: './reviewer.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./reviewer.component.scss']
})
export class ReviewerComponent implements OnInit {
  @ViewChild(SyncfusionPdfViewerComponent) public pdfviewerControl!: SyncfusionPdfViewerComponent;

  @Input()
  nodeEntry: NodeEntry;

  nodeId: string = '';
  nodeName: string = '';

  public base64String: string | undefined;
  public document: string = '';
  public loadContent: boolean = false;

  toolbarSettings = Toolbar_NoAnnotation;

  thumbnailsHidden = true;

  // Watermark
  lineOne = 'DO NOT COPY';
  lineTwo = '';
  maxFontSizeLineOne = 25;
  maxFontSizeLineTwo = 20;
  colorLineOne = '#818589';
  colorLineTwo = '#A9A9A9';
  opacityLineOne = 0.4;
  opacityLineTwo = 0.4;
  displayLayout = '';
  maxColumns = 3;
  rightTabOpen = false;

  configWaterEnable = false;

  constructor(
    private reviewerService: ReviewerService,
    private notificationService: NotificationService,
    private configExtensionService: ConfigExtensionService,
    private peopleContentService: PeopleContentService
  ) {}

  async ngOnInit() {

    // Initialize loading spinner
    this.initializeSpinner();

    // Load watermark configuration
    await this.loadWatermarkConfig();

    // Setup watermark with user information
    this.setupWatermarkWithUserInfo();

    // Process document for viewing
    if (this.nodeEntry) {
      await this.processDocument();
    }
  }

  /**
   * Initializes the loading spinner
   */
  private initializeSpinner(): void {
    createSpinner({
      target: document.getElementById('spinner'),
      width: '70px',
      label: 'Loading.'
    });
  }

  /**
   * Loads watermark configuration from service
   */
  private async loadWatermarkConfig(): Promise<void> {
    try {
      const data = await this.configExtensionService.getWatermark();
      if (data?.review) {
        this.applyWatermarkConfig(data);
      }
    } catch (error) {
      console.error('Error getting watermark config:', error);
    }
  }

  /**
   * Applies watermark configuration to component properties
   */
  private applyWatermarkConfig(data: any): void {
    this.maxFontSizeLineOne = data.review.maxFontSizeLineOne;
    this.maxFontSizeLineTwo = data.review.maxFontSizeLineTwo;
    this.colorLineOne = data.review.colorLineOne;
    this.colorLineTwo = data.review.colorLineTwo;
    this.opacityLineOne = data.review.opacityLineOne;
    this.opacityLineTwo = data.review.opacityLineTwo;
    this.displayLayout = data.review.displayLayout;
    this.maxColumns = data.review.maxColumns;
    this.rightTabOpen = false;
    this.configWaterEnable = true;
    this.lineOne = data.lineOne;
    this.lineTwo = data.lineTwo;
  }

  /**
   * Sets up watermark with current user information
   */
  private setupWatermarkWithUserInfo(): void {
    this.peopleContentService.getCurrentUserInfo().subscribe((userInfo) => {
      const watermarkData = {
        ...userInfo,
        ...this.nodeEntry.entry.properties,
        'cm:name': this.nodeEntry.entry.name
      };

      this.generateWatermark(watermarkData);
    });
  }

  /**
   * Processes the current document for viewing
   */
  private async processDocument(): Promise<void> {
    this.nodeId = this.nodeEntry.entry.id;
    this.nodeName = this.nodeEntry.entry.name;

    const nodeVersion = this.nodeEntry.entry.properties['cm:versionLabel'];

    // Handle PDF files directly, or create PDF rendition for other file types
    if (this.isPdfFile()) {
      this.loadDocument(nodeVersion);
    } else {
      await this.handleNonPdfFile();
    }
  }

  /**
   * Checks if the current file is a PDF
   */
  private isPdfFile(): boolean {
    return this.nodeName.toLowerCase().endsWith('.pdf');
  }

  /**
   * Handles processing of non-PDF files by creating/checking renditions
   */
  private async handleNonPdfFile(): Promise<void> {
    const renId = 'pdf';
    showSpinner(document.getElementById('spinner'));

    // Check if rendition already exists
    if (await this.checkExistingRendition(renId)) {
      return;
    }

    // Create new rendition
    if (!await this.createNewRendition(renId)) {
      return;
    }

    // Wait for rendition to be ready
    await this.waitForRenditionReady(renId);
  }

  /**
   * Checks if a rendition already exists for the file
   */
  private async checkExistingRendition(renditionId: string): Promise<boolean> {
    const statusCheck = await this.reviewerService.getCheckRenditions(this.nodeId, renditionId);
    if (statusCheck) {
      this.loadRenditionContent(renditionId);
      return true;
    }
    return false;
  }

  /**
   * Creates a new rendition for the file
   */
  private async createNewRendition(renditionId: string): Promise<boolean> {
    const statusCreate = await this.reviewerService.createRenditionData(this.nodeId, renditionId);
    if (!statusCreate) {
      this.alertWarning('Failed to create PDF rendition');
      hideSpinner(document.getElementById('spinner'));
      return false;
    }
    return true;
  }

  /**
   * Waits for a rendition to be ready with retry logic
   */
  private async waitForRenditionReady(renditionId: string): Promise<void> {
    const maxRetriesStatusCheck = 10;

    for (let attempt = 0; attempt < maxRetriesStatusCheck; attempt++) {
      const statusCheck = await this.reviewerService.getRetryCheckRenditions(this.nodeId, renditionId);
      if (statusCheck) {
        this.loadRenditionContent(renditionId);
        return;
      }
      await this.sleep(5000); // 5 second delay
    }

    // If we've exhausted all retries and still no success
    hideSpinner(document.getElementById('spinner'));
    this.alertWarning('Failed to generate PDF rendition after multiple attempts');
  }

  private async loadRenditionContent(renId: any) {
    await setTimeout(async () => {
      const base64Data = await this.reviewerService.getRenditionContent(this.nodeId, renId);
      if (base64Data) {
        hideSpinner(document.getElementById('spinner'));
        this.base64String = base64Data;
        this.loadContent = true;
      } else {
        hideSpinner(document.getElementById('spinner'));
        this.alertWarning('Failed to load document content');
      }
    }, 1000);
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async loadDocument(versionId: any) {
    showSpinner(document.getElementById('spinner'));

    if (versionId) {
      return new Promise(() => {
        setTimeout(async () => {
          const base64Data = await this.reviewerService.downloadNodeVersion(this.nodeId, versionId);
          if (base64Data) {
            hideSpinner(document.getElementById('spinner'));
          } else {
            hideSpinner(document.getElementById('spinner'));
            this.alertWarning('Failed to download document version');
          }
          this.base64String = base64Data;
        }, 1000);
      });
    }
    return new Promise(async () => {
      const base64Data = await this.reviewerService.downloadNode(this.nodeId);
      if (base64Data) {
        hideSpinner(document.getElementById('spinner'));
      } else {
        hideSpinner(document.getElementById('spinner'));
        this.alertWarning('Failed to download document');
      }
      this.base64String = base64Data;
    });
  }

  generateWatermark(value: any) {
    const date = moment().format('DD/MM/YYYY');
    const time = moment().format('hh:mm:ss');
    // const lineOne = this.appConfigService.get<string>('watermark.lineOne') || '';
    // const lineTwo = this.appConfigService.get<string>('watermark.lineTwo') || '';
    this.lineOne = this.template(this.lineOne, { ...value, date, time });
    this.lineTwo = this.template(this.lineTwo, { ...value, date, time });
  }

  template(tpl: any, args: any) {
    return tpl.replace(/\${([A-Za-z0-9.,:-]*)}/g, (_: any, v: any) => {
      return args[v] ? args[v] : '';
    });
  }

  alertInfo(infoTxt: string) {
    this.notificationService.showInfo(infoTxt, '', { duration: 1000 }, true);
  }

  alertWarning(infoTxt: string) {
    this.notificationService.showError(infoTxt, '', { duration: 1000 }, true);
  }
}
